import React, { useEffect, useRef } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AvatarWithCircle } from "../user-menu-trigger/user-avatar/avatar-with-circle";
import { useStatisticsStore } from "../../stores/statisticsStore";
import { CustomTypography } from "../custom-typography/custom-typography";
import { CustomMarkdown } from "../custom-markdown/custom-markdown";
import useUserStore from "../../stores/userStore";
import useAppStore from "../../stores/appStore";

import {
  containerStyle,
  textStyle,
  lineStyle,
  getAvatarWrapperStyle,
  getPlaceTextStyle,
  getAvatarStyle,
} from "./styles";
import { LeaderBoardItem } from "../../types/statistics";

interface LeaderBoardProps {
  isStatic?: boolean;
  currentUserAvatarRef?: React.RefObject<HTMLDivElement | null>;
}

export const LeaderBoard: React.FC<LeaderBoardProps> = ({
  isStatic = false,
  currentUserAvatarRef,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const prevUserIdRef = useRef<string | null>(null);
  const {
    leadersBoard: dynamicLeadersBoard,
    leadersBoardText: dynamicLeadersBoardText,
    fetchLeadersBoard,
    fetchPioneersClub,
    refreshedLeaderBoard,
    setRefreshedLeaderBoard,
    setRefreshedProgressBar,
    clearStatistics,
  } = useStatisticsStore();
  const { user } = useUserStore();
  const { userAvatar } = useAppStore();

  const staticLeadersBoard = [
    {
      user: { user_id: 2, name: "Alice", avatar: null },
      score: 4.6,
      place: 1,
    },
    {
      user: { user_id: user.id, name: user.first_name, avatar: null },
      score: 4,
      place: 2,
    },
    {
      user: { user_id: 1, name: "M", avatar: null },
      score: 3.7,
      place: 3,
    },
    {
      user: { user_id: 3, name: "Bob", avatar: null },
      score: 1.8,
      place: 4,
    },
    {
      user: { user_id: 4, name: "A", avatar: null },
      score: 0.4,
      place: 5,
    },
  ];

  const staticLeadersBoardText =
    "You are **number 2📈. You are only 7 correct answers** away 💪 from surpassing 🏆 the next racer 🏃‍♂️.";

  const leadersBoard = isStatic ? staticLeadersBoard : dynamicLeadersBoard;
  const leadersBoardText = isStatic
    ? staticLeadersBoardText
    : dynamicLeadersBoardText;

  const formatMobileLeaderText = (text: string) =>
    text
      .replace(/(the next racer)\s+/i, "$1\u00A0")
      .replace(
        /\s+([\p{Extended_Pictographic}][\p{Extended_Pictographic}\uFE0F\u200D]*)(\.?)$/u,
        "\u00A0$1$2",
      );

  useEffect(() => {
    if (isStatic) return;
    if (!user?.id) return;

    const prevUserId = prevUserIdRef.current;
    prevUserIdRef.current = user.id;

    // Only clear on a real user switch. Remount (prevUserId === null) must not
    // treat already-fetched stats as stale — that doubled API calls after reload.
    if (prevUserId !== null && prevUserId !== user.id) {
      clearStatistics();
      setRefreshedProgressBar(true);
      setRefreshedLeaderBoard(true);
    }
  }, [
    isStatic,
    user?.id,
    clearStatistics,
    setRefreshedProgressBar,
    setRefreshedLeaderBoard,
  ]);

  useEffect(() => {
    if (!isStatic && refreshedLeaderBoard) {
      // Clear the flag first so concurrent remounts / effects don't re-fetch.
      setRefreshedLeaderBoard(false);
      fetchLeadersBoard();
      fetchPioneersClub();
    }
  }, [
    isStatic,
    refreshedLeaderBoard,
    fetchLeadersBoard,
    fetchPioneersClub,
    setRefreshedLeaderBoard,
  ]);

  if (!leadersBoard || leadersBoard.length === 0) {
    return null;
  }

  const scores = leadersBoard.map((item) => item.score);
  const minScore = Math.min(...scores);
  const maxScore = Math.max(...scores);

  const currentUserScore =
    (leadersBoard as LeaderBoardItem[]).find(
      (item: LeaderBoardItem) => item.user.user_id === user.id
    )?.score ?? null;

  if (currentUserScore === null) {
    return null;
  }

  const getOffsetPercent = (score: number) => {
    // Position based on actual score relative to all participants
    // minScore -> 5%, maxScore -> 95%
    if (maxScore === minScore) {
      return 50;
    }
    const ratio = (score - minScore) / (maxScore - minScore);
    return 5 + ratio * 90;
  };

  let itemsWithOffset = leadersBoard.map((item, index) => ({
    item,
    offsetPercent: getOffsetPercent(item.score),
    originalIndex: index,
  }));

  itemsWithOffset.sort((a, b) => a.offsetPercent - b.offsetPercent);

  const MIN_DISTANCE_PERCENT = isMobile ? 9 : 4;

  for (let pass = 0; pass < 5; pass++) {
    itemsWithOffset.sort((a, b) => {
      if (a.offsetPercent !== b.offsetPercent) {
        return a.offsetPercent - b.offsetPercent;
      }
      const diff = a.item.score - b.item.score;
      if (diff !== 0) return diff;
      return a.originalIndex - b.originalIndex;
    });

    for (let i = 1; i < itemsWithOffset.length; i++) {
      const prev = itemsWithOffset[i - 1];
      const curr = itemsWithOffset[i];
      const distance = curr.offsetPercent - prev.offsetPercent;
      if (distance < MIN_DISTANCE_PERCENT) {
        curr.offsetPercent = prev.offsetPercent + MIN_DISTANCE_PERCENT;
      }
    }
  }

  const minSafePositionPercent = 4;
  const maxSafePositionPercent = isMobile ? 88 : 92;

  itemsWithOffset.forEach((item) => {
    item.offsetPercent = Math.max(
      minSafePositionPercent,
      Math.min(item.offsetPercent, maxSafePositionPercent)
    );
  });

  itemsWithOffset.sort((a, b) => {
    if (a.offsetPercent !== b.offsetPercent) {
      return a.offsetPercent - b.offsetPercent;
    }
    const diff = a.item.score - b.item.score;
    if (diff !== 0) return diff;
    return a.originalIndex - b.originalIndex;
  });

  for (let pass = 0; pass < 5; pass++) {
    for (let i = 1; i < itemsWithOffset.length; i++) {
      const prev = itemsWithOffset[i - 1];
      const curr = itemsWithOffset[i];
      const distance = curr.offsetPercent - prev.offsetPercent;
      if (distance < MIN_DISTANCE_PERCENT) {
        const newPosition = prev.offsetPercent + MIN_DISTANCE_PERCENT;
        curr.offsetPercent = Math.min(newPosition, maxSafePositionPercent);

        if (curr.offsetPercent >= maxSafePositionPercent && i > 1) {
          let canShift = 0;
          for (let j = i - 1; j >= 1; j--) {
            const prevItem = itemsWithOffset[j - 1];
            const currItem = itemsWithOffset[j];
            const dist = currItem.offsetPercent - prevItem.offsetPercent;
            if (dist > MIN_DISTANCE_PERCENT * 1.2) {
              canShift = Math.min(
                canShift + (dist - MIN_DISTANCE_PERCENT * 1.2),
                MIN_DISTANCE_PERCENT
              );
            }
          }
          if (canShift > 0) {
            for (let j = i - 1; j >= 0; j--) {
              itemsWithOffset[j].offsetPercent = Math.max(
                minSafePositionPercent,
                itemsWithOffset[j].offsetPercent - canShift
              );
            }
            curr.offsetPercent = Math.min(
              itemsWithOffset[i - 1].offsetPercent + MIN_DISTANCE_PERCENT,
              maxSafePositionPercent
            );
          }
        }
      }
    }

    itemsWithOffset.sort((a, b) => {
      if (a.offsetPercent !== b.offsetPercent) {
        return a.offsetPercent - b.offsetPercent;
      }
      const diff = a.item.score - b.item.score;
      if (diff !== 0) return diff;
      return a.originalIndex - b.originalIndex;
    });
  }

  const offsetCountMap = new Map<number, number>();
  const SHIFT_BASE = isMobile ? 0 : 29;

  return (
    <Box sx={containerStyle}>
      <Typography sx={textStyle}>
        <CustomMarkdown
          children={
            isMobile ? formatMobileLeaderText(leadersBoardText) : leadersBoardText
          }
        />
      </Typography>

      <Box sx={lineStyle} />

      {itemsWithOffset.map((obj, index) => {
        const { item, offsetPercent } = obj;
        const isCurrentUser = item.user.user_id === user.id;
        const avatarSize = isMobile
          ? isCurrentUser
            ? "24px"
            : "18px"
          : isCurrentUser
            ? "32px"
            : "24px";

        const roundedOffset = parseFloat(offsetPercent.toFixed(2));
        const existingCount = offsetCountMap.get(roundedOffset) || 0;
        offsetCountMap.set(roundedOffset, existingCount + 1);

        const shiftPx = existingCount * SHIFT_BASE;

        return (
          <Box
            ref={isCurrentUser ? currentUserAvatarRef : null}
            key={index}
            sx={getAvatarWrapperStyle(offsetPercent, shiftPx)}
          >
            <CustomTypography sx={getPlaceTextStyle(isCurrentUser)}>
              {item.place}
            </CustomTypography>
            <AvatarWithCircle
              size={avatarSize}
              sx={getAvatarStyle(isCurrentUser, isMobile)}
              name={isCurrentUser ? undefined : item.user.name}
              img={isCurrentUser ? userAvatar : item.user.avatar || ""}
              userId={String(item.user.user_id)}
              hideText={true}
              showOnlyLevel={true}
              useGlobalTrigger={true}
              isCurrentUser={isCurrentUser}
              score={item.score}
              minScore={minScore}
              maxScore={maxScore}
              place={item.place}
              totalParticipants={leadersBoard.length}
            />
          </Box>
        );
      })}
    </Box>
  );
};
